// Three detonation looks of mockup round 065, group B "the sprawl" and group
// C (R2079, `ui-map-interface.md` §6.3): card 11 TIDE FRONT, the AUTO look,
// card 18 SPORE DRIFT and card 29 DRAG PARTICLES.
//
// Each recipe is the card's `draw(c, t, p)` line for line, through the kit
// (`detonation-recipe-kit.ts` states the translation). Every figure that
// sets the TIME or the WEIGHT of a look stands in its `fig` table of
// `data/presentation/detonation.toml`, read by name through `figs`; the
// shape of a curve stays here, the rule of `detonation-recipes-contained.ts`.
// The two buff forms of mockup round 131, `screen` and `surge`, stand in
// the last section, one heading a card (R2842).

import {
  DET_LIFE_SEC, TAU, INK, band, buffEnv, cl, circle, clamp01, curtain, disc, dots, fwd, glow2, glow4,
  inside, lerp, line, once, outCubic, px, reachOf, rnd, seed, smooth,
  figs, withBuffForms, type DetonationCtx, type DetonationRecipe,
} from './detonation-recipe-kit';

const { white: WHITE, fire: FIRE, amber: AMBER, hot: HOT, plume: PLUME, foam: FOAM, steel: STEEL } = INK;

/** The heat ramp of §6.3 in five steps by age: groups B and C. */
const HEAT = [WHITE, PLUME, AMBER, FIRE, HOT] as const;
function heat(u: number): number {
  return HEAT[Math.min(4, Math.max(0, Math.floor(u * 5)))];
}

/** A centre flash for a blast, inside R: group B's `flash`. */
function flash(c: DetonationCtx, R: number, a: number): void {
  if (a <= 0.005) return;
  glow4(c, 0, 0, R, 0, WHITE, 0.9 * a, 0.35, PLUME, 0.6 * a, 0.75, FIRE, 0.25 * a, 1, HOT, 0);
}

/**
 * A stroked circle centred on `r` with width `w`, cut at `R`: the card's
 * `circle` under its clip. The part past `R` is cut off, so the stroke
 * that stays runs from its inner edge to `R`.
 */
function circleIn(c: DetonationCtx, r: number, ink: number, a: number, w: number, R: number): void {
  const lo = r - w / 2, hi = Math.min(R, r + w / 2);
  if (hi <= lo || hi <= 0) return;
  circle(c, 0, 0, (Math.max(0, lo) + hi) / 2, ink, a, hi - Math.max(0, lo));
}

// ---------------------------------------------------------------------------
// Card 11, TIDE FRONT
// ---------------------------------------------------------------------------

/**
 * One wavefront leaves the impact at a constant speed. A thin bright line
 * leads, and a soft wake follows it. A wide splash takes longer to fill.
 * The line stops on the splash radius and leaves a dotted rim that fades.
 * A blast has a hot wake and a white flash at the centre. Slow drags three
 * thin lines behind the front, and they settle on the rim. Sweep puts ticks
 * on the front and runs a light along the rim. Repair sends a second line
 * back to the centre.
 */
function tideFront(c: DetonationCtx): void {
  const F = figs(c, 'tide-front');
  const R = reachOf(c), blast = c.form === 'blast', t = c.t, k = c.form;
  const A = weight(c);
  const T = blast ? F.blast_front_sec + c.R / F.blast_px_per_sec
    : c.R > 0 ? F.cool_front_sec + c.R / F.cool_px_per_sec : F.point_front_sec;
  const hold = k === 'blast' ? F.blast_hold_sec : k === 'slow' ? F.slow_hold_sec
    : k === 'sweep' ? F.sweep_hold_sec : F.repair_hold_sec;
  const end = Math.min(DET_LIFE_SEC - 0.05, T + hold);
  if (t > end) return;
  const u = clamp01(t / T), rf = R * u, since = t - T, v = R / T;
  if (blast) flash(c, R * F.flash_share, A * (1 - smooth(F.flash_from_sec, F.flash_to_sec, t)));
  // The wake: a soft band behind the front. It piles up on the rim and fades.
  const wake = Math.min(rf, v * (blast ? F.blast_wake_sec : F.cool_wake_sec)) * (1 - smooth(T, T + F.wake_fade_sec, t));
  if (wake > 0.2) {
    if (blast) {
      band(c, rf - wake, rf, HOT, 0, F.hot_wake_alpha * A);
      band(c, rf - wake * 0.5, rf, AMBER, 0, F.amber_wake_alpha * A);
    } else {
      band(c, rf - wake, rf, c.ink, 0, F.cool_wake_alpha + F.cool_wake_power_alpha * c.power + (k === 'slow' ? F.slow_wake_alpha : 0));
    }
  }
  // The front line.
  const fa = A * (since < 0 ? 1 : 1 - smooth(T, T + F.front_fade_sec, t)), rl = Math.min(rf, R - px(c, 0.8));
  if (fa > 0.01 && rl > 0) {
    if (blast) {
      circleIn(c, rl, AMBER, fa * 0.7, px(c, F.blast_glow_screen_px + F.blast_glow_power_screen_px * c.power), R);
      circleIn(c, rl, WHITE, fa, px(c, 1.2), R);
    } else {
      const pulse = k === 'sweep' ? 0.6 + 0.4 * Math.sin(t * 30) : 1;
      circleIn(c, rl, c.ink, fa * 0.35 * pulse, px(c, 3.2), R);
      circleIn(c, rl, FOAM, fa * pulse, px(c, 1.2), R);
    }
  }
  if (k === 'slow') {
    // Three lines that lag behind the front and settle on the rim one by one.
    for (let j = 1; j <= 3; j++) {
      const lag = j * (F.slow_lag_sec + T * 0.12), uk = clamp01((t - lag) / T), rk = Math.min(R * uk, R - px(c, 0.8));
      const ak = A * 0.7 * (1 - j * 0.2) * (uk < 1 ? 1 : 1 - smooth(T + lag, T + lag + 0.35, t));
      if (rk > 0 && ak > 0.01) circleIn(c, rk, STEEL, ak, px(c, 1), R);
    }
  }
  if (k === 'sweep' && since < 0.25) {
    // Short ticks on the front, as the marks of a sonar scope.
    const nt = 16 + Math.floor(R / 6), tl = Math.min(5, rf * 0.3);
    for (let i = 0; i < nt; i++) {
      const a = (i * TAU) / nt, ca = Math.cos(a), sa = Math.sin(a);
      line(c, ca * (rl - tl), sa * (rl - tl), ca * rl, sa * rl, STEEL, fa * 0.8, px(c, 1), R);
    }
  }
  if (k === 'repair' && since > 0) {
    // The return line: the tide goes back to the centre.
    const ur = clamp01((since - 0.1) / (T * 0.9 + 0.2)), rr = R * (1 - ur);
    if (ur > 0 && ur < 1) {
      circleIn(c, Math.min(rr, R - px(c, 0.8)), FOAM, A * 0.9 * Math.sin(Math.PI * ur), px(c, 1.2), R);
      // The band runs out from the line, and the card's clip cuts it at R.
      const out = rr + Math.min(R * 0.2, 14), a0 = 0.12 * Math.sin(Math.PI * ur);
      const hi = Math.min(R, out);
      band(c, rr, hi, c.ink, a0, a0 * (1 - (hi - rr) / (out - rr)));
    }
  }
  // The dotted rim: it stays when the front stops.
  if (since > -0.05) {
    const ra = smooth(T - 0.05, T + 0.05, t) * (1 - smooth(T + 0.15, end, t));
    const gap = Math.max(px(c, 5), (TAU * R) / 90), col = blast ? AMBER : k === 'sweep' ? FOAM : c.ink;
    dots(c, R - px(c, 1.2), col, ra * (blast ? F.blast_rim_alpha : F.cool_rim_alpha), px(c, 2), gap, k === 'slow' ? 0 : -since * 6);
    if (k === 'sweep') {
      // A light that runs once along the rim, clockwise from north.
      const hu = clamp01(since / 0.9), a1 = -Math.PI / 2 + TAU * hu;
      if (hu > 0 && hu < 1) dots(c, R - px(c, 1.2), WHITE, 0.8 * ra, px(c, 2), gap, 0, a1 - 0.5, 0.5);
    }
  }
}

/** The weight of groups B: a blast is bright, a power-0 effect thin, and a
 *  point form bright so it reads (the card's `wt`). */
function weight(c: DetonationCtx): number {
  if (c.R === 0) return 0.9;
  return c.form === 'blast' ? 0.55 + 0.45 * c.power : 0.3 + 0.55 * c.power;
}

// ---------------------------------------------------------------------------
// Card 18, SPORE DRIFT
// ---------------------------------------------------------------------------

/** One mote: its place, its distance, three random shares and the point a
 *  repair mote drifts to. */
interface Mote { x: number; y: number; d: number; a: number; b: number; c: number; tx: number; ty: number }

/** The motes of card 18 on a jittered grid: the card's `motes`. The card
 *  sends a repair mote to the nearest own hull in R; the map hands a look
 *  no hull, so a repair mote drifts to the centre (R2079). */
function motesOf(R: number, R0: number, pw: number, s0: number): Mote[] {
  const rho = 0.004 + 0.012 * pw;
  let g = Math.min(1 / Math.sqrt(rho), R / 2.2);
  g = Math.max(g, R * Math.sqrt(Math.PI / 260));
  if (R0 === 0) g = 2.6;
  seed(s0 + 18);
  const out: Mote[] = [];
  for (let x = -R; x <= R + 1e-6; x += g) {
    for (let y = -R; y <= R + 1e-6; y += g) {
      const mx = x + (rnd() - 0.5) * 0.9 * g, my = y + (rnd() - 0.5) * 0.9 * g, d = Math.hypot(mx, my);
      const a = rnd(), b = rnd(), cc = rnd();
      if (d >= R * 0.97) continue;
      out.push({ x: mx, y: my, d, a, b, c: cc, tx: 0, ty: 0 });
    }
  }
  return out;
}

/** The motes of the round a context names. */
function motesFor(c: DetonationCtx): Mote[] {
  return motesOf(reachOf(c), c.R, c.power, c.seed);
}

/**
 * Glowing motes fill the splash radius on a jittered grid, so the spread is
 * even and has no clumps. A soft front places them from the centre outward;
 * then they drift slowly and fade. Slow motes sink. Repair motes drift in
 * and go out. A blast throws embers from the centre out to the splash
 * radius with short streaks, from white to red. Sweep motes flash when a
 * ping ring passes them. The damage sets the density.
 */
function sporeDrift(c: DetonationCtx): void {
  const F = figs(c, 'spore-drift');
  const R = reachOf(c), A = weight(c), k = c.form, t = c.t;
  const T = k === 'blast' ? F.blast_front_sec + c.R / F.blast_px_per_sec
    : c.R > 0 ? F.cool_front_sec + c.R / F.cool_px_per_sec : F.point_front_sec;
  const ms = once(18, c, motesFor);
  const end = k === 'blast' ? F.blast_end_sec : k === 'sweep' ? F.sweep_end_sec : DET_LIFE_SEC - 0.05;
  if (t > end) return;
  const sz = 0.7 + 0.3 * Math.min(1, R / 40), minCore = px(c, 0.8);
  if (k === 'blast') {
    flash(c, R * F.flash_share, A * (1 - smooth(0, F.flash_sec, t)));
    for (let i = 0; i < ms.length; i++) {
      const m = ms[i], life = F.ember_sec + F.ember_spread_sec * m.a + F.ember_power_sec * c.power;
      if (t > life) continue;
      const tt = t - 0.02 * m.b, f = 0.06 + 0.94 * outCubic(tt / T), f0 = 0.06 + 0.94 * outCubic((tt - 0.04) / T);
      const q = t / life, a = A * (1 - smooth(0.3, 1, q)), col = heat(0.2 + 0.8 * q);
      line(c, m.x * f0, m.y * f0, m.x * f, m.y * f, col, a * 0.7, px(c, 1.2), R);
      const r = Math.max(minCore, (0.6 + 0.5 * m.c) * sz);
      if (inside(m.x * f, m.y * f, r, R)) disc(c, m.x * f, m.y * f, r, col, a);
    }
    return;
  }
  const col = k === 'repair' ? c.ink : k === 'sweep' ? FOAM : STEEL, halo = k === 'slow' ? c.ink : FOAM;
  const base = F.mote_alpha + F.mote_weight_alpha * A;
  for (let i = 0; i < ms.length; i++) {
    const m = ms[i], ts = (T * m.d) / R;
    if (t < ts) continue;
    let x = m.x, y = m.y, a = base * smooth(ts, ts + 0.35, t), glowUp = 0;
    if (k === 'slow') {
      y += (F.sink_px_per_sec + F.sink_spread_px_per_sec * m.c) * (t - ts); x += Math.sin(t * 0.9 + m.a * 6) * 1.2;
      a *= 1 - smooth(2.5 + 0.5 * m.a, 3.5, t);
    } else if (k === 'repair') {
      const q = smooth(ts + 0.3, ts + 1.9 + 0.5 * m.a, t);
      x = lerp(m.x, m.tx, q * 0.95) + Math.sin(t * 1.3 + m.b * 6) * (1 - q); y = lerp(m.y, m.ty, q * 0.95);
      a *= 1 - smooth(0.75, 1, q);
      a *= 1 - smooth(3.1, 3.5, t);
    } else {
      x += Math.sin(t * 0.7 + m.a * 6) * 1.5; y += Math.cos(t * 0.6 + m.b * 6) * 1.5;
      a *= 0.55 * (1 - smooth(2.2, 2.9, t));
      for (let w = 0; w < 2; w++) {
        const rw = ((t - 0.15 - w * 1.1) * R) / 0.9;
        if (rw < 0 || rw > R + 8) continue;
        const z = (m.d - rw) / (3 + R * 0.04);
        glowUp = Math.max(glowUp, Math.exp(-z * z));
      }
      a = Math.min(1, a + glowUp * 0.7 * (1 - smooth(2.2, 2.9, t)));
    }
    const dd = Math.hypot(x, y);
    a *= 1 - smooth(R - Math.max(3, R * 0.06), R, dd);
    if (a <= 0.01) continue;
    // A mote the card's clip would cut at the rim is left out.
    const hs = (1.6 + 1.2 * m.c) * sz * (1 + glowUp * 0.8), core = Math.max(minCore, (0.55 + 0.45 * m.c) * sz);
    if (inside(x, y, hs, R)) disc(c, x, y, hs, halo, a * F.halo_alpha);
    if (inside(x, y, core, R)) disc(c, x, y, core, glowUp > 0.3 ? WHITE : col, a);
  }
}

// ---------------------------------------------------------------------------
// Card 29, DRAG PARTICLES
// ---------------------------------------------------------------------------

/**
 * Particles burst out from the impact. Each has a drag that stops it
 * exactly on the splash radius, so the particles pile up on the rim and the
 * edge appears as a bright ring of settled dots. Then they fade. Damage
 * sets the count and the speed. A blast throws fast hot sparks. Slow
 * particles creep out on long trails and sag inward as they dim. Sweep
 * particles fly in even spokes, flash on arrival, and the ring then turns.
 * Repair particles settle, then return to the centre and gather.
 */
function dragParticles(c: DetonationCtx): void {
  const F = figs(c, 'drag-particles');
  const Rr = reachOf(c), P = c.power, k = c.form, blast = k === 'blast', t = c.t;
  const dur = blast ? F.blast_sec + F.blast_power_sec * P + c.R / F.blast_px_per_sec : F.cool_sec;
  const E = blast ? 1 - smooth(dur * 0.45, dur, t) : 1 - smooth(F.cool_fade_sec, F.cool_sec, t);
  if (E <= 0) return;
  const tau = blast ? F.blast_drag_sec + F.blast_light_drag_sec * (1 - P)
    : k === 'slow' ? F.slow_drag_sec : k === 'sweep' ? F.sweep_drag_sec : F.repair_drag_sec;
  const N = c.R === 0 ? F.point_count : Math.round((F.base_count + Rr * F.count_per_px) * (0.7 + 1.3 * P));
  seed(c.seed + 29);
  const dr = cl(Rr * 0.02, 0.6, 1.3) + 0.6 * P, rot = k === 'sweep' ? F.turn_rad * smooth(0.8, 3.2, t) : 0;
  if (blast && t < F.flash_sec) {
    glow2(c, 0, 0, Rr * (0.3 + 0.4 * P), 0, WHITE, 0.9 * (1 - t / F.flash_sec), 1, FIRE, 0);
  }
  const back = k === 'repair' ? smooth(1.7, 2.9, t) : 0;
  const sI = Rr - dr - 0.3;
  for (let i = 0; i < N; i++) {
    const a = (k === 'sweep' ? (TAU * i) / N : (TAU * (i + rnd() * 0.8)) / N) + rot;
    const ti = tau * (0.8 + 0.4 * rnd());
    const ca = Math.cos(a), sa = Math.sin(a);
    let r = sI * (1 - Math.exp(-t / ti));
    let r0 = sI * (1 - Math.exp(-Math.max(0, t - (k === 'slow' ? 0.35 : 0.05)) / ti));
    if (k === 'slow') { const sag = 1 - 0.07 * smooth(1.4, 3.2, t); r *= sag; r0 *= sag; }
    if (back > 0) { r *= 1 - back; r0 = r + (sI - r) * 0.2 * (back < 1 ? 1 : 0); }
    let hex: number;
    if (blast) { const age = clamp01(t / dur); hex = t < 0.08 ? WHITE : heat(0.15 + 0.85 * age); }
    else if (k === 'slow') hex = STEEL;
    else hex = c.ink;
    const arrived = r > sI * 0.97;
    if (r - r0 > 0.3) line(c, ca * r0, sa * r0, ca * r, sa * r, hex, F.trail_alpha * E, px(c, 0.8 + P), Rr);
    const dcol = arrived && !blast ? WHITE : hex;
    let dd = dr;
    if (k === 'sweep') {
      const f = clamp01((r / sI - 0.97) / 0.03), ping = arrived ? 1 - smooth(0, 0.35, t - ti * 3.5) : 0;
      // The card's clip cuts a ping that grows past R; the disc stops at R.
      dd = Math.min(dr * (1 + 0.8 * ping * f), Rr - r);
    }
    disc(c, ca * r, sa * r, dd, dcol, (arrived ? 0.95 : 0.7) * E);
  }
  if (back > 0.8) glow2(c, 0, 0, Math.min(Rr * 0.3, 10), 0, WHITE, 0.8 * E * smooth(0.8, 1, back), 1, c.ink, 0);
}

// ---------------------------------------------------------------------------
// The buff forms of mockup round 131 (R2842)
// ---------------------------------------------------------------------------
//
// Each pair below is the `buff.screen` and the `buff.surge` of one card of
// `archive/mockups/131-torpedo-buff-forms.html`, line for line through the
// kit: a screen settles calm, a surge pushes out, harder forward (`fwd`).

// --- card 11 ------------------------------------------------------------------

/** The calm veil of the screen trails the front by this share of R, and
 *  the bubbles of its curtain take this scale. */
const TIDE_VEIL_SHARE = 0.35;
const TIDE_CURTAIN_SCALE = 0.85;

/**
 * The tide comes in and stays. A soft, wide front with no bright core
 * fills the disc and leaves a calm veil behind it. On the rim a curtain of
 * fine bubbles takes the place of the dotted rim.
 */
function tideFrontScreen(c: DetonationCtx): void {
  const F = figs(c, 'tide-front');
  const R = reachOf(c), A = weight(c), t = c.t, T = F.screen_front_sec + R / F.screen_px_per_sec;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const rf = R * clamp01(t / T), since = t - T;
  // The calm veil behind a soft front. The fill lies under the band, so it
  // rides the quad sink too.
  glow2(c, 0, 0, rf, 0, FOAM, F.screen_fill_alpha * e, 1, FOAM, F.screen_fill_alpha * e);
  band(c, Math.max(0, rf - R * TIDE_VEIL_SHARE), rf, c.ink, 0,
    F.screen_wake_alpha * e * (1 - 0.5 * smooth(T, T + F.screen_calm_sec, t)));
  const fa = F.screen_front_alpha * A * e * (since < 0 ? 1 : 1 - 0.6 * smooth(T, T + F.screen_front_fade_sec, t));
  if (rf > 2) circleIn(c, Math.min(rf, R - px(c, 2)), FOAM, fa, px(c, 3.5), R);
  // The curtain in place of the dotted rim.
  if (since > -0.1) {
    curtain(c, R, t, c.seed + 111, F.screen_curtain_alpha * e * smooth(T - 0.1, T + 0.3, t), TIDE_CURTAIN_SCALE);
  }
}

/** The surge takes this many whiskers, plus one each this many px of R. */
const WHISKER_BASE = 18;
const WHISKER_PX = 4;

/**
 * One fast, sharp front reaches R. Behind it, whisker streaks run out on
 * rays and into the rim, longer on the forward side, and a second, weaker
 * push follows.
 */
function tideFrontSurge(c: DetonationCtx): void {
  const F = figs(c, 'tide-front');
  const R = reachOf(c), A = weight(c), t = c.t, T = F.surge_front_sec + R / F.surge_px_per_sec;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const rf = R * clamp01(t / T), since = t - T;
  // The sharp front.
  const fa = A * (since < 0 ? 1 : 1 - smooth(T, T + F.surge_front_fade_sec, t)), rl = Math.min(rf, R - px(c, 0.8));
  if (fa > 0.01 && rl > 0) {
    circleIn(c, rl, c.ink, fa * 0.35, px(c, 3), R);
    circleIn(c, rl, WHITE, fa, px(c, 1.1), R);
  }
  // The whiskers: they outrun the front and run into the rim; two pushes.
  const n = WHISKER_BASE + Math.floor(R / WHISKER_PX), w = px(c, 1);
  for (let q = 0; q < 2; q++) {
    for (let i = 0; i < n; i++) {
      const a = ((i + 0.5 * q) / n) * TAU, f = fwd(c, a);
      const u = (t - q * F.surge_push_sec) / (lerp(F.surge_slow_whisker_sec, F.surge_fast_whisker_sec, f) + q * 0.1);
      if (u < 0 || u >= 1) continue;
      const head = R * outCubic(u) - px(c, 0.8), L = R * (0.15 + 0.3 * f) * (1 - smooth(0.75, 1, u));
      const tail = Math.max(0, head - L);
      if (head - tail < 0.3) continue;
      const ca = Math.cos(a), sa = Math.sin(a);
      line(c, ca * tail, sa * tail, ca * head, sa * head, q ? c.ink : FOAM,
        A * e * (q ? F.surge_echo_ratio : F.surge_whisker_ratio) * (1 - smooth(0.8, 1, u)), w, R);
    }
  }
}

// --- card 18 ------------------------------------------------------------------

/**
 * The motes come in from the centre outward, drift a little and stop. Each
 * one swells into a soft halo while its core dims, and the halos merge into
 * one veil that holds still.
 */
function sporeDriftScreen(c: DetonationCtx): void {
  const F = figs(c, 'spore-drift');
  const R = reachOf(c), A = weight(c), t = c.t, T = F.screen_front_sec + R / F.screen_px_per_sec;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const ms = once(18, c, motesFor);
  const sz = 0.7 + 0.3 * Math.min(1, R / 40), base = F.mote_alpha + F.mote_weight_alpha * A, minCore = px(c, 0.8);
  for (let i = 0; i < ms.length; i++) {
    const m = ms[i], age = t - (T * m.d) / R;
    if (age < 0) continue;
    const still = 1 - Math.exp(-age * 1.5);
    const x = m.x + Math.sin(m.a * 6) * 1.5 * still, y = m.y + Math.cos(m.b * 6) * 1.5 * still;
    const swell = smooth(0, F.screen_swell_sec, age), hs = (1.6 + 1.2 * m.c) * sz * (1 + 1.4 * swell);
    let a = base * e * smooth(0, 0.35, age);
    a *= 1 - smooth(R - hs - 1.5, R - 0.5, Math.hypot(x, y) + hs * 0.5);
    if (a <= 0.01) continue;
    // A halo or a core the card's clip would cut at the rim is left out.
    const core = Math.max(minCore, (0.55 + 0.45 * m.c) * sz * (1 - 0.5 * swell));
    if (inside(x, y, hs, R)) disc(c, x, y, hs, FOAM, a * F.screen_halo_alpha * (1 + 0.3 * swell));
    if (inside(x, y, core, R)) disc(c, x, y, core, c.ink, a * (1 - 0.6 * swell));
  }
}

/**
 * The motes light on their grid at once, wait a moment, then speed up
 * outward with streak tails and go out at R. The forward motes are the
 * fastest, and the field is empty after about 1.5 s.
 */
function sporeDriftSurge(c: DetonationCtx): void {
  const F = figs(c, 'spore-drift');
  const R = reachOf(c), A = weight(c), t = c.t, T = F.surge_front_sec + R / F.surge_px_per_sec;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const ms = once(18, c, motesFor);
  const sz = 0.7 + 0.3 * Math.min(1, R / 40), base = F.mote_alpha + F.mote_weight_alpha * A;
  const minCore = px(c, 0.8), w = px(c, 1), home = Number.isNaN(c.course) ? 0 : c.course;
  for (let i = 0; i < ms.length; i++) {
    const m = ms[i], age = t - (T * m.d) / R;
    if (age < 0) continue;
    const ang = m.d > 0.5 ? Math.atan2(m.y, m.x) : home, f = fwd(c, ang);
    const acc = R * lerp(F.surge_slow_accel_ratio, F.surge_fast_accel_ratio, f);
    const la = age - (F.surge_wait_sec + F.surge_wait_spread_sec * m.a);
    const s = la > 0 ? 0.5 * acc * la * la : 0, d = m.d + s, lb = Math.max(0, la - 0.07), d0 = m.d + 0.5 * acc * lb * lb;
    if (d > R - 1) continue;
    const a = base * e * smooth(0, 0.15, age) * (1 - smooth(R * 0.8, R - 1, d)), ca = Math.cos(ang), sa = Math.sin(ang);
    if (a <= 0.01) continue;
    if (d - d0 > 0.3) line(c, ca * d0, sa * d0, ca * d, sa * d, FOAM, F.surge_streak_alpha * a, w, R);
    const core = Math.max(minCore, (0.55 + 0.45 * m.c) * sz);
    if (inside(ca * d, sa * d, core, R)) disc(c, ca * d, sa * d, core, la > 0 ? FOAM : c.ink, a);
  }
}

// --- card 29 ------------------------------------------------------------------

/**
 * The particles drift out slowly with no trail. As each one nears the rim
 * its drag stops it and it swells into a soft blob, so the rim becomes a
 * soft curtain of blobs over a faint veil.
 */
function dragParticlesScreen(c: DetonationCtx): void {
  const F = figs(c, 'drag-particles');
  const Rr = reachOf(c), t = c.t, tau = F.screen_drag_sec;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const N = Math.round(F.screen_particle_count + Rr * F.screen_particle_per_px);
  seed(c.seed + 2929);
  const hr = cl(Rr * 0.07, 1.8, 4), sI = Rr - hr - 0.5;
  disc(c, 0, 0, Rr * (1 - Math.exp(-t / tau)), FOAM, F.screen_veil_alpha * e);
  for (let i = 0; i < N; i++) {
    const a = (TAU * (i + rnd() * 0.8)) / N, ti = tau * (0.8 + 0.4 * rnd());
    const r = sI * (1 - Math.exp(-t / ti)), arr = r / sI, halo = lerp(0.8, hr, smooth(0.6, 1, arr));
    const x = Math.cos(a) * r, y = Math.sin(a) * r;
    disc(c, x, y, halo, FOAM, F.screen_halo_alpha * e * smooth(0, 0.3, t));
    disc(c, x, y, 0.7, c.ink, F.screen_core_alpha * e * (1 - 0.7 * smooth(0.8, 1, arr)));
  }
}

/** The three waves of the surge, each weaker. */
const DRAG_WAVES = [1, 0.7, 0.45] as const;
/** The head of a surge particle: a dot of this radius. */
const DRAG_HEAD_PX = 0.8;

/**
 * The particles have no drag. They fly out at a constant speed with long
 * streaks and do not stop: each one goes out on the rim. Three waves follow
 * each other, and the forward particles are the fastest.
 */
function dragParticlesSurge(c: DetonationCtx): void {
  const F = figs(c, 'drag-particles');
  const Rr = reachOf(c), t = c.t;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const N = Math.round(F.surge_particle_count + Rr * F.surge_particle_per_px), w = px(c, 1);
  seed(c.seed + 2930);
  for (let k = 0; k < 3; k++) {
    for (let i = 0; i < N; i++) {
      const a = (TAU * (i + rnd() * 0.8)) / N, f = fwd(c, a);
      const u = (t - k * F.surge_wave_gap_sec - rnd() * 0.05) / lerp(F.surge_slow_sec, F.surge_fast_sec, f);
      if (u < 0 || u >= 1) continue;
      const r = (Rr - 0.6) * u, r0 = Math.max(0, r - Rr * (0.18 + 0.3 * f)), al = e * DRAG_WAVES[k] * (1 - smooth(0.82, 1, u));
      const ca = Math.cos(a), sa = Math.sin(a);
      line(c, ca * r0, sa * r0, ca * r, sa * r, c.ink, F.surge_trail_alpha * al, w, Rr);
      // The card's clip cuts a head on the rim; the dot stops at R.
      disc(c, ca * r, sa * r, Math.min(DRAG_HEAD_PX, Rr - r), FOAM, F.surge_head_alpha * al);
    }
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, DetonationRecipe>> = {
  'tide-front': withBuffForms(tideFront, tideFrontScreen, tideFrontSurge),
  'spore-drift': withBuffForms(sporeDrift, sporeDriftScreen, sporeDriftSurge),
  'drag-particles': withBuffForms(dragParticles, dragParticlesScreen, dragParticlesSurge),
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
